import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useLang } from '../../../../../../hooks/use-lang';
import { DashboardCardDataType } from '../../../../../../store/api/dashboard-api';
import { useDashboardStore } from '../../../../../../store/use-dashboard-store';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';

type PossibleKeys = keyof Omit<DashboardCardDataType, 'waiter'>;

type DataType = {
  series: { name: PossibleKeys; data: number[] }[];
  categories: string[];
};

const chartTypes: Record<PossibleKeys, string> = {
  scans: 'line',
  devices: 'line',
  tasks: 'column',
  orders: 'column',
  reviews: 'column',
};

const recordTranslate: Record<PossibleKeys, string> = {
  scans: 'Всего сканирований',
  devices: 'Уникальных устройств',
  tasks: 'Задач',
  orders: 'Заказов',
  reviews: 'Отзывов',
};

export const useQrStats = (): ChartProps => {
  const { t } = useLang();
  const { list } = useDashboardStore();
  const data = list?.qr_stats as DataType;

  const chartData = useMemo<Partial<ChartProps>>(() => {
    if (data) {
      const { categories, series } = data;

      return {
        series: series.map((item) => ({
          ...item,
          type: chartTypes[item.name],
          name: t(recordTranslate[item.name]),
        })),
        type: 'line',
        options: {
          responsive: [
            {
              breakpoint: 1080,
              options: {
                chart: {
                  width: '100%',
                },
                legend: {
                  position: 'top',
                  horizontalAlign: 'left',
                },
              },
            },
            {
              breakpoint: 768,
              options: {
                chart: {
                  width: '800px',
                },
              },
            },
          ],
          chart: {
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: false,
              allowMouseWheelZoom: false,
            },

            type: 'line',
          },
          xaxis: {
            categories: categories.map((item) => dayjs(item).format('DD.MM.YYYY')),
          },
          stroke: {
            width: [4, 4, 4],
            curve: 'smooth',
          },
          plotOptions: {
            bar: {
              columnWidth: '25%',
            },
          },
          tooltip: {
            shared: true,
            intersect: false,
          },
          fill: {
            opacity: [1, 1, 1],
            gradient: {
              shade: 'light',
              type: 'vertical',
              opacityFrom: 1,
              opacityTo: 0.555,
              stops: [0, 100, 100, 100],
            },
          },
          yaxis: {
            title: {
              text: t('Кол-во'),
            },
          },
          colors: ['#28A745', '#FFC107', '#CB3CFF', '#0E43FB', '#00C2FF'],
          legend: {
            position: 'right',
          },
        },
      };
    }

    return {
      series: [],
      options: {},
    };
  }, [t, data]);

  return {
    label: 'Активность QR-кодов',
    subLabel:
      'Дашборд отображает количество сканирований QR-кодов и уникальных устройств, которые их отсканировали. А также количество задач, заказов и отзывов, которые были созданы с помощью QR-кодов.',
    type: 'line',
    series: chartData.series || [],
    options: chartData.options || {},
  };
};
